import {
  fetchContactsCard,
  fetchContactsChart,
  fetchContactsTable,
  fetchContactsBySource,
  fetchContactsByGender,
  fetchContactDrilldown,
} from './actions';
import { getDefaultAtsFilters, buildReportsSlice } from '../common/ats-filters';
import { getTableData } from '../common/flat-transformer';
import { cardTransformer } from '../common/card-transformer';
import { visualPayload } from '../common/visual-transformer';
import { startCase } from 'lodash-es';
import { emptyValue } from '@/store/atsReports/common/flat-transformer';
import { getPercentage } from '../common/get-percentage';

const getChartData = (response) => {
  const { table = [], headers, lookups: { dt, g2 } = {} } = response?.data;

  const xAxis = [...dt];

  const yAxis = g2.map((source, index) => ({
    data: Array(xAxis.length).fill(0),
    name:
      source?.name || (source ? startCase(source) : emptyValue?.appl_source),
    id: source?.id || null,
  }));

  const valueIndex = headers.indexOf('contacts_created');

  // Populate the data points
  table.forEach((weekRow) => {
    const weekIndex = weekRow[1]; // Week index (0, 1, 2, etc.)
    const sources = weekRow[0]; // Nested array of source data

    // Process each source entry for this week
    sources.forEach((sourceData) => {
      const g2Index = sourceData[3]; // g2 index (0, 1, 2, 3)
      const value = sourceData[valueIndex]; // contacts_created value

      // If g2Index is valid and within our array bounds
      if (g2Index !== null && g2Index >= 0 && g2Index < yAxis.length) {
        yAxis[g2Index].data[weekIndex] = value;
      }
    });
  });

  return {
    xAxis,
    yAxis,
  };
};

const tableWithTotal = (data) => {
  const contacts_created_total = data?.reduce(
    (acc, curr) => acc + curr?.contacts_created,
    0
  );

  function innerFunc(data) {
    if (!data?.length) return [];
    return data.map((datum) => {
      return {
        ...datum,

        contacts_created_total,
        contacts_created_per: getPercentage(
          datum?.contacts_created,
          contacts_created_total
        ),
        subRows: innerFunc(datum?.subRows),
      };
    });
  }

  return innerFunc(data);
};

const constructDrilldownData = (payload) => {
  const k1Map = payload?.data?.lookups?.k1;
  const k2Map = payload?.data?.lookups?.k2;
  const k3Map = payload?.data?.lookups?.k3;
  const k4Map = payload?.data?.lookups?.k4;
  const returnData = payload?.data?.table?.map((data) => {
    // eslint-disable-next-line no-unused-vars
    const [, k1, k2, k3, k4] = data;
    return {
      ...k1Map?.[k1],
      source: k2Map?.[k2],
      credited_to: k3Map?.[k3],
      created_by: k4Map?.[k4],
    };
  });

  return returnData;
};

const initialState = {
  ...getDefaultAtsFilters(),

  group: {
    group_by_1: null,
    group_by_2: null,
  },

  cards: {},
  isCardLoading: false,

  chart: {},
  isChartLoading: false,

  table: [],
  isTableLoading: false,

  contactsBySource: [],
  isContactsBySourceLoading: false,

  contactsByGender: [],
  isContactsByGenderLoading: false,
  isDrillLoading: false,
  initialLoading: false,
  drilldownData: [],
  stageMapping: {},
  totalCount: 0,
  currentDrillPage: 1,
};

const extraReducers = {
  [fetchContactsCard.pending]: (state) => {
    state.isCardLoading = true;
  },
  [fetchContactsCard.fulfilled]: (state, { payload }) => {
    state.cards = cardTransformer(payload);
    state.isCardLoading = false;
  },
  [fetchContactsCard.rejected]: (state) => {
    state.isCardLoading = false;
  },

  [fetchContactsTable.pending]: (state) => {
    state.isTableLoading = true;
  },
  [fetchContactsTable.fulfilled]: (state, { meta, payload = {} }) => {
    state.isTableLoading = false;
    state.table = tableWithTotal(getTableData(payload, meta?.arg));
  },
  [fetchContactsTable.rejected]: (state) => {
    state.isTableLoading = false;
  },

  [fetchContactsChart.pending]: (state) => {
    state.isChartLoading = true;
  },
  [fetchContactsChart.fulfilled]: (state, { payload = {} }) => {
    state.isChartLoading = false;
    state.chart = getChartData(payload);
  },
  [fetchContactsChart.rejected]: (state) => {
    state.isChartLoading = false;
  },

  [fetchContactsBySource.pending]: (state) => {
    state.isContactsBySourceLoading = true;
  },

  [fetchContactsBySource.fulfilled]: (state, { payload = {} }) => {
    state.contactsBySource = visualPayload(payload);
    state.isContactsBySourceLoading = false;
  },

  [fetchContactsBySource.rejected]: (state) => {
    state.isContactsBySourceLoading = false;
  },

  [fetchContactsByGender.pending]: (state) => {
    state.isContactsByGenderLoading = true;
  },
  [fetchContactsByGender.fulfilled]: (state, { payload }) => {
    state.contactsByGender = visualPayload(payload);
    state.isContactsByGenderLoading = false;
  },
  [fetchContactsByGender.rejected]: (state) => {
    state.isContactsByGenderLoading = false;
  },
  [fetchContactDrilldown.pending]: (state, { meta }) => {
    const { drilldown } = meta?.arg || {};
    if (drilldown.page === 1) {
      state.initialLoading = true;
    } else {
      state.isDrillLoading = true;
    }
  },
  [fetchContactDrilldown.fulfilled]: (state, { meta, payload = {} }) => {
    if (!payload) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;

    const { drilldown } = meta?.arg || {};
    const newData = constructDrilldownData(payload);

    state.totalCount = payload?.data?.pagination?.total_count;

    if (drilldown.page === 1) {
      state.drilldownData = newData;
    } else {
      state.drilldownData = [...state.drilldownData, ...newData];
    }
    state.currentDrillPage = drilldown.page;
  },
  [fetchContactDrilldown.rejected]: (state, action) => {
    if (
      action.error?.name === 'AbortError' ||
      action.error?.name === 'CanceledError'
    ) {
      return;
    }
    state.isDrillLoading = false;
    state.initialLoading = false;
  },
};

export const { reducer, actions } = buildReportsSlice({
  name: 'atsContactsReport',
  initialState,
  extraReducers,
});

export const {
  applyFilter,
  setFiltersData,
  resetFilter,
  applyTimeFrame,
  applyViewBy,
  applyGroup,
  applySearch,
  resetDrillDown,
} = actions;
export default reducer;
